// Lightweight form validation that produces i18n error keys (not raw text).
// Pair with `useForm` for boilerplate-free forms.

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mexican-friendly phone: digits, spaces, +, (, ), -; 10-15 digits total
const PHONE_DIGITS_RE = /\d/g;

export function required(value) {
    if (value === null || value === undefined) return "validation.required";
    if (typeof value === "string" && value.trim() === "") return "validation.required";
    if (Array.isArray(value) && value.length === 0) return "validation.required";
    return null;
}

export function email(value) {
    if (!value) return null;
    return EMAIL_RE.test(String(value).trim()) ? null : "validation.email";
}

export function phone(value) {
    if (!value) return null;
    const digits = (String(value).match(PHONE_DIGITS_RE) || []).length;
    if (digits < 10 || digits > 15) return "validation.phone";
    return null;
}

export function minLength(n, key = "validation.minLength") {
    return (v) => (v && String(v).length < n ? key : null);
}

export function passwordMin(v) {
    if (!v) return null;
    return String(v).length < 6 ? "validation.passwordMin" : null;
}

export function numberPositive(v) {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return "validation.numberInvalid";
    return n <= 0 ? "validation.numberPositive" : null;
}

export function numberNonNegative(v) {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return "validation.numberInvalid";
    return n < 0 ? "validation.numberNonNegative" : null;
}

export function integerPositive(v) {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isNaN(n) || !Number.isInteger(n)) return "validation.numberInvalid";
    return n <= 0 ? "validation.numberPositive" : null;
}

export function yearReasonable(v) {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    const current = new Date().getFullYear();
    if (!Number.isInteger(n) || n < 1980 || n > current + 1) return "validation.yearRange";
    return null;
}

export function compose(...validators) {
    return (v) => {
        for (const fn of validators) {
            const r = fn(v);
            if (r) return r;
        }
        return null;
    };
}

// Run a fields → validators map against a values object.
// Returns an errors object { field: errorKey or null }.
export function runValidators(values, fieldValidators) {
    const errors = {};
    for (const [field, fn] of Object.entries(fieldValidators)) {
        const value = values[field];
        const err = fn(value, values);
        if (err) errors[field] = err;
    }
    return errors;
}

export function hasErrors(errs) {
    return Object.values(errs || {}).some((v) => !!v);
}
